import React, { useState, useEffect, useCallback } from 'react';
import { creditoApi } from '../../../../api';
import './MisCreditos.css';

// ── Constantes ────────────────────────────────────────────────────────────────
const ESTADO_META = {
  EN_REVISION:              { label: 'En revisión documental',        color: '#2471a3', bg: '#eaf4fb' },
  VERIFICANDO_REQUISITOS:   { label: 'Verificando requisitos',        color: '#7d3c98', bg: '#f5eef8' },
  VERIFICANDO_CONDICIONES:  { label: 'Verificando condiciones',       color: '#7d3c98', bg: '#f5eef8' },
  PENDIENTE_TASACION:       { label: 'Pendiente de tasación',         color: '#2471a3', bg: '#eaf4fb' },
  PENDIENTE_APROBACION:     { label: 'Pendiente de aprobación',       color: '#7d3c98', bg: '#f5eef8' },
  APROBADO:                 { label: 'Aprobado',                      color: '#1e8449', bg: '#eafaf1' },
  RECHAZADO:                { label: 'Rechazado',                     color: '#c0392b', bg: '#fdf2f2' },
  CANCELADO:                { label: 'Cancelado',                     color: '#666',    bg: '#f3f3f3' },
  DESEMBOLSADO:             { label: 'Desembolsado – crédito activo', color: '#1a4e8a', bg: '#e8f0fb' },
};

const CUOTA_META = {
  PENDIENTE:    { label: 'Pendiente',    color: '#d68910', bg: '#fef9e7' },
  PAGADO:       { label: 'Pagado',       color: '#1e8449', bg: '#eafaf1' },
  VENCIDO:      { label: 'Vencido',      color: '#c0392b', bg: '#fdf2f2' },
  REFINANCIADO: { label: 'Refinanciado', color: '#7d3c98', bg: '#f5eef8' },
};

// ── Helpers ───────────────────────────────────────────────────────────────────
const estadoBadge = (estado, labelOverride) => {
  const m = ESTADO_META[estado] || { label: estado ?? '—', color: '#888', bg: '#f3f3f3' };
  return (
    <span className="cred-estado-badge" style={{ color: m.color, background: m.bg }}>
      {labelOverride || m.label}
    </span>
  );
};

const fmt = (n) => parseFloat(n || 0).toLocaleString('es-PE', { minimumFractionDigits: 2 });
const formatDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString('es-PE', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

// ── PlanCuotas ────────────────────────────────────────────────────────────────
const PlanCuotas = ({ cuotas, total }) => {
  const [expandido, setExpandido] = useState(false);
  if (!cuotas?.length) return null;
  const visible  = expandido ? cuotas : cuotas.slice(0, 5);
  const pagadas  = cuotas.filter(c => c.estado === 'PAGADO').length;
  const vencidas = cuotas.filter(c => c.estado === 'VENCIDO').length;

  return (
    <div className="dash-card">
      <div className="cred-cuotas-header">
        <h3 className="cred-section-title">
          <i className="fa-solid fa-calendar-days"></i> Plan de Cuotas
          <span className="cred-count">{total ?? cuotas.length}</span>
        </h3>
        <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
          {pagadas  > 0 && <span className="cred-cuota-badge" style={{ color:'#1e8449', background:'#eafaf1' }}><i className="fa-solid fa-check"></i> {pagadas} pagadas</span>}
          {vencidas > 0 && <span className="cred-cuota-badge" style={{ color:'#c0392b', background:'#fdf2f2' }}><i className="fa-solid fa-triangle-exclamation"></i> {vencidas} vencidas</span>}
          <span className="cred-cuota-badge" style={{ color:'#888', background:'#f3f3f3' }}><i className="fa-solid fa-clock"></i> {cuotas.length - pagadas - vencidas} pendientes</span>
        </div>
      </div>

      <div className="cred-cuotas-table-wrap">
        <table className="cred-cuotas-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Fecha pago</th>
              <th>Cuota</th>
              <th>Capital</th>
              <th>Interés</th>
              <th>Saldo final</th>
              <th>Pendiente</th>
              <th>Estado</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((c) => {
              const meta = CUOTA_META[c.estado] || CUOTA_META.PENDIENTE;
              return (
                <tr key={c.id ?? c.numero_cuota} className={`cred-cuota-row cred-cuota-row--${(c.estado || '').toLowerCase()}`}>
                  <td className="cred-cuota-num">{c.numero_cuota}</td>
                  <td>{formatDate(c.fecha_pago)}</td>
                  <td><strong>S/ {fmt(c.monto_cuota)}</strong></td>
                  <td>S/ {fmt(c.amortizacion)}</td>
                  <td>S/ {fmt(c.interes)}</td>
                  <td>S/ {fmt(c.saldo_final)}</td>
                  <td>S/ {fmt(c.monto_pendiente)}</td>
                  <td>
                    <span className="cred-cuota-badge" style={{ color: meta.color, background: meta.bg }}>
                      {meta.label}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {cuotas.length > 5 && (
        <button className="cred-cuotas-toggle" onClick={() => setExpandido(e => !e)}>
          <i className={`fa-solid ${expandido ? 'fa-chevron-up' : 'fa-chevron-down'}`}></i>
          {expandido ? 'Ver menos' : `Ver las ${cuotas.length - 5} cuotas restantes`}
        </button>
      )}
    </div>
  );
};

// ── CreditoCard (en lista) ────────────────────────────────────────────────────
const CreditoCard = ({ c, onDetail, loadingDetail }) => {
  const isDesembolsado = c.estado === 'DESEMBOLSADO';
  const isAprobado     = c.estado === 'APROBADO';
  const isRechazado    = c.estado === 'RECHAZADO';
  const tipoNombre     = c.tipo_credito?.nombre || '';
  const detNombre      = c.detalle_tipo_credito?.nombre || '';
  const pct = c.cuotas_resumen
    ? Math.round((c.cuotas_resumen.pagadas / c.cuotas_resumen.total) * 100)
    : null;

  const borderCls = isDesembolsado ? ' cred-card--desembolsado'
                  : isAprobado     ? ' cred-card--aprobado'
                  : isRechazado    ? ' cred-card--rechazado'
                  : '';

  return (
    <div className={`cred-card${borderCls}`}>
      <div className="cred-card__top">
        <div className="cred-card__left">
          <span className="cred-card__code">
            <i className="fa-solid fa-credit-card"></i> {c.codigo_credito}
          </span>
          {(tipoNombre || detNombre) && (
            <span className="cred-card__tipo">{tipoNombre}{tipoNombre && detNombre ? ' · ' : ''}{detNombre}</span>
          )}
        </div>
        <div className="cred-card__right">
          {estadoBadge(c.estado, c.estado_label)}
          <span className="cred-card__date">{formatDate(c.fecha_entrega)}</span>
        </div>
      </div>

      <div className="cred-card__amounts">
        <div><label>Monto</label><strong>S/ {fmt(c.monto)}</strong></div>
        <div><label>Cuotas</label><strong>{c.num_cuotas} {c.frecuencia_pago}</strong></div>
        <div><label>Tasa</label><strong>{c.tasa_interes}% TEM</strong></div>
        {c.fecha_desembolso && <div><label>Desembolso</label><strong>{formatDate(c.fecha_desembolso)}</strong></div>}
      </div>

      {isDesembolsado && c.cuotas_resumen && pct !== null && (
        <div className="cred-cuotas-prog">
          <div className="cred-cuotas-prog__label">
            <span>Avance de cuotas</span>
            <span>{c.cuotas_resumen.pagadas}/{c.cuotas_resumen.total}</span>
          </div>
          <div className="cred-prog-bar">
            <div className="cred-prog-fill" style={{ width: `${pct}%`, background: pct === 100 ? '#27ae60' : '#1a4fa0' }}></div>
          </div>
        </div>
      )}

      <div className="cred-card__footer">
        <button className="cred-detail-btn" onClick={() => onDetail(c.id)} disabled={loadingDetail}>
          <i className="fa-solid fa-eye"></i> Ver detalle
        </button>
      </div>
    </div>
  );
};

// ── CreditoDetail ─────────────────────────────────────────────────────────────
const CreditoDetail = ({ c, onBack }) => {
  const isDesembolsado = c.estado === 'DESEMBOLSADO';
  const isAprobado     = ['APROBADO','DESEMBOLSADO'].includes(c.estado);
  const isRechazado    = c.estado === 'RECHAZADO';
  const tipoNombre     = c.tipo_credito?.nombre || '';
  const detNombre      = c.detalle_tipo_credito?.nombre || '';
  const rs             = c.resumen_cuotas;

  return (
    <div className="cred-detail">
      <button className="dash-back-btn" onClick={onBack}>
        <i className="fa-solid fa-arrow-left"></i> Volver
      </button>

      {/* Header */}
      <div className="cred-detail__header">
        <div>
          <div className="cred-detail__code">
            <i className="fa-solid fa-credit-card"></i> {c.codigo_credito}
          </div>
          {(tipoNombre || detNombre) && (
            <div className="cred-detail__tipo">{tipoNombre}{tipoNombre && detNombre ? ' — ' : ''}{detNombre}</div>
          )}
        </div>
        <div className="cred-detail__right">
          {estadoBadge(c.estado, c.estado_label)}
          {c.fecha_aprobacion && (
            <span className="cred-detail__date">
              <i className="fa-solid fa-calendar-check"></i> {formatDate(c.fecha_aprobacion)}
            </span>
          )}
        </div>
      </div>

      {/* Banner estado */}
      {isAprobado && (
        <div className={`cred-banner cred-banner--${isDesembolsado ? 'active' : 'ok'}`}>
          <i className={`fa-solid ${isDesembolsado ? 'fa-money-bill-wave' : 'fa-circle-check'}`}></i>
          <div className="cred-banner__body">
            <strong>{isDesembolsado ? 'Crédito activo y desembolsado' : '¡Crédito aprobado!'}</strong>
            <p>Monto: <strong>S/ {fmt(c.monto)}</strong> · {c.num_cuotas} cuotas {c.frecuencia_pago} · {c.tasa_interes}% TEM</p>
          </div>
        </div>
      )}
      {isRechazado && (
        <div className="cred-banner cred-banner--rejected">
          <i className="fa-solid fa-circle-xmark"></i>
          <div className="cred-banner__body">
            <strong>Crédito rechazado</strong>
            {c.motivo_rechazo && <p>{c.motivo_rechazo}</p>}
            {!c.motivo_rechazo && <p>Contacta con nosotros para más información.</p>}
          </div>
        </div>
      )}

      {/* Datos principales */}
      <div className="dash-card">
        <div className="cred-detail__grid">
          <div><label>Monto</label><strong>S/ {fmt(c.monto)}</strong></div>
          <div><label>N° cuotas</label><strong>{c.num_cuotas}</strong></div>
          <div><label>Frecuencia</label><strong>{c.frecuencia_pago}</strong></div>
          <div><label>Tasa TEM</label><strong>{c.tasa_interes}%</strong></div>
          {c.fecha_entrega && <div><label>Fecha entrega</label><strong>{formatDate(c.fecha_entrega)}</strong></div>}
          {c.fecha_desembolso && <div><label>Desembolsado</label><strong>{formatDate(c.fecha_desembolso)}</strong></div>}
          {c.progreso_requisitos != null && <div><label>Prog. req.</label><strong>{c.progreso_requisitos}%</strong></div>}
          {c.progreso_condiciones != null && <div><label>Prog. cond.</label><strong>{c.progreso_condiciones}%</strong></div>}
        </div>
      </div>

      {/* Resumen cuotas */}
      {rs && (
        <div className="dash-card">
          <h3 className="cred-section-title"><i className="fa-solid fa-chart-pie"></i> Resumen de cuotas</h3>
          <div className="cred-resumen-strip">
            <div className="cred-resumen-item">
              <span className="cred-resumen-item__val">{rs.total}</span>
              <span className="cred-resumen-item__lbl">Total</span>
            </div>
            <div className="cred-resumen-item cred-resumen-item--ok">
              <span className="cred-resumen-item__val">{rs.pagadas}</span>
              <span className="cred-resumen-item__lbl">Pagadas</span>
            </div>
            <div className="cred-resumen-item cred-resumen-item--warn">
              <span className="cred-resumen-item__val">{rs.pendientes}</span>
              <span className="cred-resumen-item__lbl">Pendientes</span>
            </div>
            {rs.vencidas > 0 && (
              <div className="cred-resumen-item cred-resumen-item--rej">
                <span className="cred-resumen-item__val">{rs.vencidas}</span>
                <span className="cred-resumen-item__lbl">Vencidas</span>
              </div>
            )}
            {rs.porcentaje_avance != null && (
              <div className="cred-resumen-item">
                <span className="cred-resumen-item__val" style={{ color: rs.porcentaje_avance === 100 ? '#27ae60' : '#1a4fa0' }}>
                  {rs.porcentaje_avance}%
                </span>
                <span className="cred-resumen-item__lbl">Avance</span>
              </div>
            )}
          </div>
          {rs.porcentaje_avance != null && (
            <div className="cred-cuotas-prog" style={{ marginTop: 12 }}>
              <div className="cred-prog-bar" style={{ height: 9 }}>
                <div className="cred-prog-fill" style={{ width: `${rs.porcentaje_avance}%`, background: rs.porcentaje_avance === 100 ? '#27ae60' : '#1a4fa0' }}></div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Plan de cuotas */}
      <PlanCuotas cuotas={c.plan_cuotas} total={c.resumen_cuotas?.total} />

      {/* Observación */}
      {c.observacion && (
        <div className="dash-card">
          <h3 className="cred-section-title"><i className="fa-solid fa-comment-lines"></i> Observaciones</h3>
          <p style={{ fontSize: '0.88rem', color: '#5a6a80', margin: 0 }}>{c.observacion}</p>
        </div>
      )}
    </div>
  );
};

// ── MisCreditos (main) ────────────────────────────────────────────────────────
const MisCreditos = () => {
  const [data, setData]                   = useState({ data: [], total: 0 });
  const [selected, setSelected]           = useState(null);
  const [loading, setLoading]             = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError]                 = useState('');
  const [page, setPage]                   = useState(1);

  const fetchCreditos = useCallback(async (p = 1) => {
    setLoading(true); setError('');
    try {
      const res = await creditoApi.getMisCreditos(p);
      setData(res);
    } catch (err) {
      if (err.status !== 401) setError(err.message || 'Error al cargar créditos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCreditos(page); }, [fetchCreditos, page]);

  const openDetail = async (id) => {
    setDetailLoading(true);
    try {
      const res = await creditoApi.getCreditoDetalle(id);
      setSelected(res);
    } catch (err) {
      if (err.status !== 401) setError(err.message || 'Error al cargar detalle');
    } finally {
      setDetailLoading(false);
    }
  };

  if (loading && !data.data?.length) return (
    <div className="dash-loading"><div className="spinner"></div></div>
  );

  const lista     = data.data || [];
  const total     = data.total ?? lista.length;
  const totalPags = data.pages ?? Math.ceil(total / 10);

  const activos = lista.filter(c => c.estado === 'DESEMBOLSADO').length;
  const aprobados = lista.filter(c => c.estado === 'APROBADO').length;

  if (selected) return (
    <div>
      <h2 className="dash-section-title">Mis Créditos</h2>
      <CreditoDetail c={selected} onBack={() => setSelected(null)} />
    </div>
  );

  return (
    <div>
      <h2 className="dash-section-title">Mis Créditos</h2>
      <p className="dash-section-sub">Todos tus créditos activos e históricos</p>

      <div className="dash-stat-strip">
        <div className="dash-stat-item">
          <div className="dash-stat-item__val">{total}</div>
          <div className="dash-stat-item__lbl">Total</div>
        </div>
        <div className="dash-stat-item">
          <div className="dash-stat-item__val" style={{ color: '#1a4fa0' }}>{activos}</div>
          <div className="dash-stat-item__lbl">Activos</div>
        </div>
        <div className="dash-stat-item">
          <div className="dash-stat-item__val" style={{ color: '#27ae60' }}>{aprobados}</div>
          <div className="dash-stat-item__lbl">Aprobados</div>
        </div>
      </div>

      {error && <div className="dash-alert dash-alert--error">{error}</div>}

      {lista.length === 0 ? (
        <div className="dash-card dash-empty">
          <i className="fa-solid fa-credit-card"></i>
          <p>No tienes créditos registrados aún.</p>
        </div>
      ) : (
        <>
          <div className="cred-list">
            {lista.map((c) => (
              <CreditoCard key={c.id} c={c} onDetail={openDetail} loadingDetail={detailLoading} />
            ))}
          </div>
          {total > 10 && (
            <div className="cred-pagination">
              <button disabled={page === 1} onClick={() => setPage(p => p - 1)}>
                <i className="fa-solid fa-chevron-left"></i>
              </button>
              <span>Página {page} de {totalPags}</span>
              <button disabled={page >= totalPags} onClick={() => setPage(p => p + 1)}>
                <i className="fa-solid fa-chevron-right"></i>
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default MisCreditos;
